import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';
import { automationApi } from '@/api/client';

/**
 * WAF scheduled reports — served by the automation service (`automationApi`,
 * NOT the DataCentral v4 API). Ruby controller `watch/reports_controller.rb`;
 * envelope is `{ status, data: { reports, pagination } }` (snake_case rows).
 * Endpoints: GET /workers/reports · POST /workers/reports/:id/resend · … .
 */

export interface WafReport {
  report_id: number;
  /** Report UUID — also the `id` the resend/history routes take. */
  id: string;
  report_uuid: string;
  report_name: string;
  report_status: string;
  report_interval: string;
  report_scope: string;
  report_type: string;
  report_priority: number;
  report_description?: string;
  service_id?: number;
  provider?: string;
  report_engine?: string;
  domains?: string[];
  domain_source?: string;
  last_send_date?: string | null;
  created_at?: string;
  updated_at?: string;
}

interface WafReportPagination {
  total?: number;
  page?: number;
  page_size?: number;
  total_pages?: number;
}

interface WafReportsEnvelope {
  status: number;
  data: { reports: WafReport[]; pagination?: WafReportPagination };
}

export interface WafReportListParams {
  page?: number;
  page_size?: number;
  id?: string;
}

/** `GET /workers/reports` — paginated WAF scheduled-report list. */
export function useWafReports(params: WafReportListParams = {}) {
  return useQuery({
    queryKey: ['waf-reports', 'list', params],
    queryFn: async (): Promise<{ reports: WafReport[]; pagination?: WafReportPagination }> => {
      const q = qs.stringify({ page: 1, page_size: 20, ...params });
      const res = await automationApi.get<WafReportsEnvelope>(`/workers/reports?${q}`);
      return res.data.data;
    },
  });
}

/** `POST /workers/reports/:id/resend` — re-send a report for a `{gte,lte,type}` window. */
export function useResendWafReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      id: string;
      gte?: string;
      lte?: string;
      type?: string;
    }): Promise<unknown> => {
      const { id, ...body } = payload;
      const res = await automationApi.post(`/workers/reports/${id}/resend`, body);
      return res.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['waf-reports'] }),
  });
}
